/**
 * Inventory.js
 *
 * Full-page inventory route at /inventory. Reuses InventoryView so it stays
 * in sync with the in-game I-key overlay (components/InventoryOverlay.js).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Package, ArrowLeft } from 'lucide-react';
import InventoryView from '../components/InventoryView';

function Inventory() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [inventory, setInventory] = useState(null);
  const [deaths, setDeaths] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, d] = await Promise.all([
        api.get('/api/player/inventory').then((r) => r.data),
        api.get('/api/player/deaths').then((r) => r.data),
      ]);
      setInventory(i);
      setDeaths(Array.isArray(d) ? d : []);
    } catch (_e) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const dropOre = async (symbol) => {
    const ores = (inventory?.ores || []).filter((o) => o.symbol !== symbol);
    try {
      const { data } = await api.put('/api/player/inventory', { ores });
      setInventory(data);
      toast.success('Ore stack discarded');
    } catch (_e) {
      toast.error('Failed to drop ore');
    }
  };

  const clearAll = async () => {
    try {
      const { data } = await api.put('/api/player/inventory', { ores: [] });
      setInventory(data);
      toast.success('Cargo cleared');
    } catch (_e) {
      toast.error('Failed to clear cargo');
    }
  };

  const recoverCache = async (cache) => {
    try {
      await api.post(`/api/player/deaths/${cache.id}/recover`, {
        pos: null,
        coords_key: cache.coords_key,
        planet_idx: cache.planet_idx,
        moon_idx: cache.moon_idx,
      });
      toast.success('Cache recovered');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to recover cache';
      toast.error(detail);
    }
  };

  const backTo = location.state?.from || '/explore/octants';

  return (
    <div className="min-h-screen pb-20">
      <Header breadcrumbs={[{ label: 'Inventory' }]} />
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Package className="w-7 h-7 text-cyan-300" /> Inventory
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Mined ores persist across sessions. Drops on death create caches you can recover on-site.
            </p>
            <p className="text-muted-foreground text-xs mt-1">
              Tip: While in-game, press <kbd className="px-1.5 py-0.5 rounded border border-cyan-400/30 text-cyan-200 bg-white/5 font-mono text-[10px]">I</kbd> to toggle inventory without exiting.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate(backTo)}
            data-testid="inventory-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        <InventoryView
          loading={loading}
          inventory={inventory}
          deaths={deaths}
          onDropOre={dropOre}
          onClearAll={clearAll}
          onRecoverCache={recoverCache}
        />
      </div>
    </div>
  );
}

export default Inventory;
